import { PageNavigation } from "@components/cms/page-navigation/PageNavigation";
import type { IPageTwoColumns } from "@contentful-api/types/contentful";
import { ModulesRenderer } from "@lib/contentful/modules-renderer/ModulesRenderer";
import { H } from "@ecco/ui/a11y-heading";
import { Box } from "@ecco/ui/box";
import { Grid, GridItem } from "@ecco/ui/grid";
import { type FC, type ReactNode } from "react";
import { getPrivacyAttributes } from "@lib/monitoring/privacyAttributes";
import type { FlexProps } from "@ecco/ui/flex";

type ColumnSlots = {
    prefix?: ReactNode;
    suffix?: ReactNode;
    order?: FlexProps["order"];
};

export type TwoColumnsPageProps = {
    page: IPageTwoColumns;
    leftColumn?: ColumnSlots;
    rightColumn?: ColumnSlots;
    navigationTitle?: string;
    showTitle?: boolean;
    showNavigation?: boolean;
    isPrivateDataInTitle?: boolean;
};

// leftColumn and rightColumn props now accepts prefix and suffix. If you pass your component as prefix it will be rendered before
// the contentful modules, and if you pass it as suffix it will be rendered after the contentful modules.

export const TwoColumnsPage: FC<TwoColumnsPageProps> = ({
    page,
    leftColumn,
    rightColumn,
    navigationTitle,
    isPrivateDataInTitle,
    showTitle = true,
    showNavigation = true,
}) => {
    const {
        fields: { leftColumnModules, rightColumnModules, pageNavigation: pageNavigationLinks } = {},
    } = page;

    return (
        <>
            {showNavigation && pageNavigationLinks && (
                <PageNavigation pageNavigationLinks={pageNavigationLinks} title={navigationTitle} />
            )}
            {/* AudioEye initialization is handled globally by AudioEyeScript */}
            <Box w="full" pt={4} pb={[8, 12, 16]} px={[3, 4, 6]} bg="gray.50" m="0 !important">
                {showTitle && (
                    <H
                        {...(isPrivateDataInTitle ? getPrivacyAttributes() : {})}
                        size="h2"
                        fontSize={["mobileH2", "desktopH2"]}
                        mb={[6, 6, 12]}
                    >
                        {page?.fields?.title || ""}
                    </H>
                )}
                <Grid templateColumns={["1fr", "1fr", "1fr", "repeat(12,1fr)"]} gap={0.2}>
                    <GridItem
                        colSpan={[1, 1, 1, 8]}
                        flexDirection="column"
                        gap={0.5}
                        order={leftColumn?.order || 0}
                    >
                        {leftColumn?.prefix}
                        <ModulesRenderer modules={leftColumnModules} wrapInsideCard />
                        {leftColumn?.suffix}
                    </GridItem>
                    <GridItem
                        colSpan={[1, 1, 1, 4]}
                        flexDirection="column"
                        gap={0.2}
                        order={rightColumn?.order || 0}
                    >
                        {rightColumn?.prefix}
                        <ModulesRenderer modules={rightColumnModules} wrapInsideCard />
                        {rightColumn?.suffix}
                    </GridItem>
                </Grid>
            </Box>
        </>
    );
};
